import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { getMyBookings } from '@/lib/booking-api';
import type { BookingResponse } from '@/lib/schemas/booking';
import { TutorBookingDetailPanel } from './TutorBookingDetailPanel';
import { Calendar, CalendarDayHeader } from '@/components/calendar';
import type { YearAndMonth } from '@/components/calendar';
import { cn } from '@/lib/utils';

const STATUS_COLORS: Record<string, string> = {
  pending:
    'bg-yellow-50 text-yellow-800 border-yellow-200 hover:bg-yellow-100 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-900/50 dark:hover:bg-yellow-950/60',
  accepted:
    'bg-green-50 text-green-800 border-green-200 hover:bg-green-100 dark:bg-green-950/40 dark:text-green-300 dark:border-green-900/50 dark:hover:bg-green-950/60',
  declined:
    'bg-red-50 text-red-800 border-red-200 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/50 dark:hover:bg-red-950/60',
  cancelled:
    'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700 dark:hover:bg-slate-800',
  completed:
    'bg-blue-50 text-blue-800 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/50 dark:hover:bg-blue-950/60',
};

export function TutorBookingsPage() {
  const { accessToken } = useAuth();
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [yearAndMonth, setYearAndMonth] = useState<YearAndMonth>(() => {
    const now = new Date();
    return [now.getFullYear(), now.getMonth() + 1];
  });

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setLoading(true);

    getMyBookings(accessToken)
      .then((data) => {
        if (!cancelled) {
          setBookings(data);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Failed to load bookings');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Bookings keyed by local date ("yyyy-MM-dd") so they land in the right day cell.
  const bookingsByDate = useMemo(() => {
    const map: Record<string, BookingResponse[]> = {};
    for (const booking of bookings) {
      const dateString = format(new Date(booking.slotStartUtc), 'yyyy-MM-dd');
      (map[dateString] ??= []).push(booking);
    }
    for (const dayBookings of Object.values(map)) {
      dayBookings.sort(
        (a, b) => new Date(a.slotStartUtc).getTime() - new Date(b.slotStartUtc).getTime(),
      );
    }
    return map;
  }, [bookings]);

  function bookingLabel(booking: BookingResponse): string {
    const title = booking.classTitle || booking.subject || 'Class';
    return booking.seriesId ? `${title} ↻` : title;
  }

  function handleUpdate(updated: BookingResponse) {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }

  const selectedBooking = selectedBookingId
    ? bookings.find((b) => b.id === selectedBookingId)
    : undefined;

  if (loading) {
    return (
      <div className="flex h-full flex-col space-y-4 animate-pulse py-2">
        {/* Page Header Skeleton */}
        <div className="shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="h-7 w-32 rounded bg-slate-200 dark:bg-slate-800" />
          </div>
        </div>

        {/* Full-page Month Calendar Skeleton */}
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 flex flex-col">
          {/* Mock Calendar Navigation Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <div className="h-9 w-20 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-8 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
            <div className="h-6 w-32 rounded bg-slate-200 dark:bg-slate-800" />
            <div className="flex items-center gap-1.5">
              <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-800" />
              <div className="h-8 w-16 rounded bg-slate-200 dark:bg-slate-800" />
            </div>
          </div>

          {/* Days of Week Header Grid */}
          <div className="grid shrink-0 grid-cols-7 border-b border-slate-200 dark:border-slate-800">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, index) => (
              <div
                key={d}
                className={cn(
                  'px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400',
                  [0, 6].includes(index) && 'text-slate-400 dark:text-slate-505',
                )}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day Grid Skeletons */}
          <div className="flex-1 grid grid-cols-7 grid-rows-5 divide-x divide-y divide-slate-100 dark:divide-slate-700/60 border-b border-slate-100 dark:divide-slate-700/60">
            {Array.from({ length: 35 }).map((_, idx) => {
              const hasBooking = idx === 11 || idx === 12 || idx === 18 || idx === 22;
              const hasBooking2 = idx === 12;
              return (
                <div key={idx} className="p-2 min-h-[80px] flex flex-col justify-between bg-white dark:bg-transparent">
                  <div className="h-4 w-4 rounded bg-slate-200 dark:bg-slate-800 self-end" />
                  {hasBooking && (
                    <div className="flex-1 flex flex-col justify-end gap-1 mt-2">
                      <div className="h-4 w-full rounded bg-brand-50/50 dark:bg-brand-950/20 border border-brand-100/10" />
                      {hasBooking2 && (
                        <div className="h-4 w-4/5 rounded bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100/10" />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col py-2 animate-fade-in">
      {/* Page Header */}
      <div className="mb-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-slate-100">
            My Classes
          </h1>
        </div>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-955/30 dark:text-red-400"
          >
            {error}
          </div>
        )}

        {!error && bookings.length === 0 && (
          <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50/50 px-4 py-4 text-center dark:border-slate-800 dark:bg-slate-900/20">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No classes yet. New bookings by parents will automatically appear here.
            </p>
          </div>
        )}
      </div>

      {/* Full-page month calendar */}
      {!error && (
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
          <Calendar
            yearAndMonth={yearAndMonth}
            onYearAndMonthChange={setYearAndMonth}
            renderDay={(calendarDayObject) => (
              <>
                <CalendarDayHeader calendarDayObject={calendarDayObject} />
                <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                  {(bookingsByDate[calendarDayObject.dateString] ?? []).map((booking) => (
                    <button
                      key={booking.id}
                      type="button"
                      onClick={() => setSelectedBookingId(booking.id)}
                      title={`${bookingLabel(booking)} · ${format(new Date(booking.slotStartUtc), 'p')}`}
                      className={cn(
                        'w-full shrink-0 truncate rounded border px-1.5 py-0.5 text-left text-[11px] font-medium leading-4 transition-colors',
                        STATUS_COLORS[booking.status] ?? STATUS_COLORS['pending'],
                      )}
                    >
                      <span className="mr-1 tabular-nums opacity-70">
                        {format(new Date(booking.slotStartUtc), 'h:mm a')}
                      </span>
                      {bookingLabel(booking)}
                    </button>
                  ))}
                </div>
              </>
            )}
          />
        </div>
      )}

      {/* Detail Panel */}
      {selectedBooking && (
        <TutorBookingDetailPanel
          booking={selectedBooking}
          onClose={() => setSelectedBookingId(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}
